"use client";

import React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { 
  Search, 
  Compass, 
  BarChart3, 
  GraduationCap, 
  ArrowRight, 
  Sparkles, 
  CheckCircle2, 
  History,
  Building2,
  BookOpen
} from "lucide-react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function Home() {
  return (
    <div className="min-h-screen bg-background selection:bg-primary/30">
      {/* Hero Section */}
      <section className="relative pt-20 pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[120px] rounded-full opacity-70" />
          <div className="absolute bottom-[10%] right-[-10%] w-[50%] h-[50%] bg-secondary/10 blur-[120px] rounded-full opacity-70" />
        </div>

        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-[2rem] border border-outline-variant/20 bg-surface-container/70 p-4 shadow-lg shadow-primary/10 backdrop-blur-md md:h-28 md:w-28"
          >
            <div className="relative h-full w-full">
              <Image
                src="/ccmt_logo.png"
                alt="CCMT Logo"
                fill
                priority
                className="object-contain"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs md:text-sm font-bold mb-8 shadow-sm backdrop-blur-md animate-pulse"
          >
            <span className="flex size-2 rounded-full bg-emerald-500" />
            <span>Latest: 2026 Round 1 Cutoffs & Allotments Now Live!</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-3xl md:text-5xl font-display font-black tracking-tight text-foreground mb-6 leading-tight"
          >
            CCMT College Predictor 2026 & <br />
            GATE Cutoff Explorer
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base md:text-lg text-on-surface-variant max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            The most trusted platform for CCMT preference filling. Strategically 
            plan your M.Tech choices with updated 2026 Round 1 cutoffs, 6 years of 
            historical precision data, integrated NIRF rankings, and our advanced 
            predictor designed to maximize your admission chances.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-4"
          >
            <Link href="/explorer">
              <Button size="lg" className="h-14 px-8 rounded-xl text-base font-bold gap-2 hover:scale-105 active:scale-95 transition-all">
                <Search className="size-5" />
                Explorer
              </Button>
            </Link>
            <Link href="/predictor">
              <Button size="lg" variant="secondary" className="h-14 px-8 rounded-xl text-base font-bold gap-2 hover:scale-105 active:scale-95 transition-all">
                <BarChart3 className="size-5" />
                Predictor
              </Button>
            </Link>
            <Link href="/institutes_and_programs">
              <Button size="lg" variant="outline" className="h-14 px-8 rounded-xl text-base font-bold gap-2 hover:scale-105 active:scale-95 transition-all">
                <GraduationCap className="size-5" />
                Institutions & Programs
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="rounded-3xl bg-surface-container border border-outline-variant/20 p-8 md:p-12 relative overflow-hidden shadow-sm"
          >
            <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 text-center relative z-10">
              <StatItem label="Data Records" value="45,000+" icon={History} />
              <StatItem label="Institutions" value="85+" icon={Building2} />
              <StatItem label="Programs" value="500+" icon={BookOpen} />
              <StatItem label="Year Range" value="2021-26" icon={Sparkles} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-4xl font-display font-black text-foreground mb-4"
            >
              Everything You Need for Perfect Preference Filling
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-on-surface-variant text-base md:text-lg max-w-xl mx-auto"
            >
              Build your choice list with confidence using our comprehensive suite 
              of tools designed specifically for CCMT aspirants.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard 
              icon={Search}
              title="Unified Explorer"
              description="Access 6 years of verified cutoff data (2021-2026) with customizable filters to understand college trends."
              delay={0.2}
              href="/explorer"
              tag="2021-2026"
            />
            <FeatureCard 
              icon={BarChart3}
              title="Precision Predictor"
              description="Get instant predictions based on your GATE score, category, and preferred group using high-fidelity analysis."
              delay={0.3}
              href="/predictor"
              tag="Popular"
            />
            <FeatureCard 
              icon={Building2}
              title="NIRF Integration"
              description="Make informed decisions by viewing official NIRF rankings directly alongside college and program data."
              delay={0.4}
              href="/institutes_and_programs"
              tag="New"
            />
            <FeatureCard 
              icon={CheckCircle2}
              title="Verified Data"
              description="Sleep easy knowing every cutoff, seat matrix, and program detail is pulled directly from verified official sources."
              delay={0.5}
              href="#verified"
              isVerified
            />
          </div>
        </div>
      </section>

      {/* SEO & Guide Section */}
      <section className="py-20 bg-surface-container/30 border-t border-outline-variant/10 relative">
        <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none" />
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold mb-4">
              <BookOpen className="size-4" />
              <span>CCMT M.Tech Counseling Guide</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-black text-foreground mb-4">
              CCMT College Predictor: Your Key to NIT, IIIT, & GFTI M.Tech Admissions
            </h2>
            <p className="text-on-surface-variant text-sm md:text-base leading-relaxed">
              Understand the nuances of GATE CCMT counseling, analyze previous cutoff patterns, and learn how to secure seat allocation in premium institutes.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Left Column: Core Predictor Info */}
            <div className="lg:col-span-2 space-y-8">
              <div className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm space-y-6">
                <h3 className="text-xl font-bold text-foreground flex items-center gap-3">
                  <span className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">1</span>
                  What is the CCMT College Predictor?
                </h3>
                <p className="text-on-surface-variant leading-relaxed text-sm md:text-base">
                  Navigating post-GATE counseling is one of the most critical—and often overwhelming—stages in an engineering graduate&apos;s journey. Thousands of seats are available across NITs, IIITs, IIEST Shibpur, and GFTIs, but decoding which programs you are realistically eligible for requires analyzing years of historical cutoff data across dozens of categories and rounds. Our platform simplifies this entirely: it is a free, data-driven tool that allows you to input your GATE score, paper code, and reservation category and instantly see which colleges and programs admitted candidates with similar credentials in previous years.
                </p>
                <p className="text-on-surface-variant leading-relaxed text-sm md:text-base">
                  What sets this predictor apart is the depth of its data foundation—6 consecutive years of verified seat allotment records from 2021 through 2026, covering over 45,000 data points across 85+ institutions. By identifying patterns in how cutoffs shift between counseling rounds, we help you build a balanced choice list with aspirational &quot;Dream&quot; colleges at the top, realistic &quot;Target&quot; options in the middle, and safe fallbacks at the bottom. No registration is required, and the entire analysis happens instantly in your browser.
                </p>
              </div>

              <div className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm space-y-6">
                <h3 className="text-xl font-bold text-foreground flex items-center gap-3">
                  <span className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">2</span>
                  Unraveling CCMT Cutoffs & Opening and Closing Ranks
                </h3>
                <p className="text-on-surface-variant leading-relaxed text-sm md:text-base">
                  CCMT counseling spans multiple rounds—typically Round 1, Round 2, Round 3, and then Special Rounds for remaining vacancies. The opening score (highest GATE score admitted) and closing score (lowest GATE score admitted, i.e., the cutoff) for each program vary significantly between these rounds. Generally, seats in more popular NITs and programs fill up in Round 1, but later rounds often present opportunities as candidates with higher scores withdraw to join other institutions. Understanding this round-by-round dynamics is essential to strategy.
                </p>
                <p className="text-on-surface-variant leading-relaxed text-sm md:text-base">
                  Cutoffs are not static—they fluctuate every year based on the GATE exam&apos;s difficulty level, the number of qualified candidates in each paper, seat matrix revisions, and candidate preference patterns. Our Explorer tool lets you track these year-on-year trends from 2021 to 2026, giving you a nuanced, data-backed view of where your score stands across multiple counseling scenarios. Use this insight to strategically choose between ambitious colleges at the top and reliable safety options at the bottom of your preference list.
                </p>
              </div>
            </div>

            {/* Right Column: College list, ranking & paper codes */}
            <div className="space-y-8">
              <div className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm space-y-6">
                <h3 className="text-xl font-bold text-foreground flex items-center gap-3">
                  <span className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">3</span>
                  CCMT College List & NIRF Rankings
                </h3>
                <p className="text-on-surface-variant leading-relaxed text-sm">
                  A vital step in counseling is understanding the participating institutes. Our tool offers a complete <strong className="text-foreground">ccmt college list</strong> containing over 80+ elite technical universities. Beyond a simple list, we provide deep insights into <strong className="text-foreground">ccmt college ranking</strong> metrics, integrating the latest government evaluations to show the <strong className="text-foreground">ccmt college nirf ranking</strong> for each campus.
                </p>
                <p className="text-on-surface-variant leading-relaxed text-sm">
                  Knowing the ranking is essential for creating a smart preference order. Choosing the right <strong className="text-foreground">ccmt college</strong> requires looking at placements, infrastructure, and cutoffs. With our <strong className="text-foreground">gate ccmt college list</strong>, you can sort institutes based on parameters that matter to you.
                </p>
              </div>

              <div className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm space-y-6">
                <h3 className="text-lg font-bold text-foreground">
                  Supported GATE Papers & Codes
                </h3>
                <p className="text-on-surface-variant leading-relaxed text-xs">
                  We support specialized predictions for key domains like <strong className="text-foreground">gate ccmt college cs</strong> (Computer Science), <strong className="text-foreground">gate ccmt college da</strong> (Data Science & AI), and <strong className="text-foreground">gate ccmt college ece</strong> (Electronics & Communication), along with all other paper disciplines:
                </p>
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {["ae", "ag", "ar", "bm", "bt", "ce", "ch", "cy", "da", "ee", "es", "gg", "in", "ma", "me", "mn", "mt", "nm", "pe", "ph", "pi", "st", "tf", "xe", "xh", "xl", "ge", "ey"].map((code) => (
                    <span key={code} className="px-2 py-0.5 rounded bg-primary/5 border border-outline-variant/10 text-[10px] font-mono text-primary font-bold uppercase">
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Card: Strategy */}
          <div className="max-w-6xl mx-auto mt-8">
            <div className="p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm space-y-6">
              <h3 className="text-xl font-bold text-foreground flex items-center gap-3">
                <span className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">4</span>
                How to Maximize Your Seat Allotment in CCMT 2026
              </h3>
              <p className="text-on-surface-variant leading-relaxed text-sm md:text-base">
                Success in CCMT 2026 counseling is not solely determined by your GATE score — it is primarily about how strategically you fill your choice list. A common and costly mistake candidates make is filling 50 choices without any logical structure. The recommended approach is to build a tiered list: put your top 5-8 &quot;Dream&quot; colleges first (even if cutoffs look high, seats may open in later rounds), followed by 15-20 realistic &quot;Target&quot; options where your score is near the historical closing range, and ending with 10-15 &quot;Safe&quot; options where you are well above the closing score. Always double-check paper code eligibility for each program — not every GATE paper qualifies for every specialization. Our Explorer lets you filter by paper code, category, and year simultaneously to remove the guesswork entirely.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description, delay, href, tag, isVerified }: any) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -10 }}
      className="group relative p-6 rounded-3xl bg-surface-container border border-outline-variant/20 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 transition-all duration-500 overflow-hidden"
    >
      {tag && (
        <div className={cn(
          "absolute top-6 right-6 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border",
          tag.toLowerCase() === "new"
            ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/35 animate-pulse"
            : tag.toLowerCase() === "popular"
            ? "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/35"
            : "bg-primary/10 text-primary border-primary/20 dark:bg-primary/25 dark:text-primary dark:border-primary/30"
        )}>
          {tag}
        </div>
      )}
      
      <div className="size-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-8 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
        <Icon className="size-8" />
      </div>
      
      <h3 className="text-xl font-display font-bold text-foreground mb-3">{title}</h3>
      <p className="text-on-surface-variant leading-relaxed mb-6 text-sm md:text-base">
        {description}
      </p>
      
      {!isVerified ? (
        <Link href={href} className="inline-flex items-center gap-2 text-primary text-sm font-bold hover:gap-3 transition-all">
          Explore Now <ArrowRight className="size-4" />
        </Link>
      ) : (
        <div className="flex items-center gap-2 text-primary text-sm font-bold">
          Official Source <CheckCircle2 className="size-4" />
        </div>
      )}
    </motion.div>
  );
}

function StatItem({ label, value, icon: Icon }: { label: string, value: string, icon: any }) {
  return (
    <motion.div 
      whileHover={{ scale: 1.05 }}
      className="flex flex-col items-center gap-3 group"
    >
      <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-1 group-hover:bg-primary/20 group-hover:text-primary transition-colors duration-500">
        <Icon className="size-5" />
      </div>
      <span className="text-3xl md:text-4xl font-display font-black text-foreground tracking-tight">{value}</span>
      <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-[0.2em]">{label}</span>
    </motion.div>
  );
}

